/**
 * Print helpers specific to cancellation quote functionality.
 * These utilities prepare quote data for print-optimized display and layout.
 */

import type {
  CancelQuoteResult,
  CancelQuoteSuccess,
  CancelQuoteDenied,
  CancelReason,
  GetCancelQuoteRequest,
} from '@/features/cancellationQuote/types';
import type { ContractDetail } from '@/features/contractSearch/types';
import {
  formatDateForPrint,
  formatCurrencyForPrint,
  getOptimalTextHandling,
  type PrintTextConfig,
} from '@/lib/printUtils';

/**
 * Interface for print-ready quote data that's optimized for table display
 */
export interface PrintQuoteData {
  // Header information
  customerName: string;
  vehicleInfo: string;
  cancelDate: string;
  cancelMileage: string;
  cancelReason: string;
  quoteDate: string;

  // Organized quote results
  successfulQuotes: PrintSuccessfulQuote[];
  deniedQuotes: PrintDeniedQuote[];
}

/**
 * Print-optimized data structure for successful quotes
 */
export interface PrintSuccessfulQuote {
  contractNumber: string;
  productType: string;
  issuingDealer: string;
  effectiveDate: string;
  planTerm: string;
  proRataFactor: string;
  originalPrice: string;
  cancellationFee: string;
  claimsDeducted: string;
  refundAmount: string;
  // Metadata for print formatting
  textHandling?: {
    issuingDealer: PrintTextConfig;
    productType: PrintTextConfig;
  };
}

/**
 * Print-optimized data structure for denied quotes
 */
export interface PrintDeniedQuote {
  contractNumber: string;
  productType: string;
  issuingDealer: string;
  denialReason: string;
  // Metadata for print formatting
  textHandling?: {
    issuingDealer: PrintTextConfig;
    denialReason: PrintTextConfig;
    productType: PrintTextConfig;
  };
}

/**
 * Options for controlling text handling in print tables
 */
export interface PrintTableOptions {
  allowTextWrapping: boolean;
  preserveCriticalContent: boolean; // If true, denial reasons will wrap instead of truncate
  maxDealerNameLength: number;
  maxDenialReasonLines: number;
}

/**
 * Default print table options optimized for 8.5x11 landscape
 */
export const DEFAULT_PRINT_OPTIONS: PrintTableOptions = {
  allowTextWrapping: true,
  preserveCriticalContent: true,
  maxDealerNameLength: 25,
  maxDenialReasonLines: 3,
};

/**
 * Prepares quote data for print layout by organizing and formatting all necessary information.
 * This function takes the raw quote results and associated data and transforms it into
 * a print-friendly format optimized for table display.
 *
 * @param quoteResults - Array of quote results from the API
 * @param submittedQuoteRequest - The original quote request data
 * @param selectedContracts - Contract details for the quotes
 * @param cancelReasons - Available cancel reasons for description lookup
 * @param options - Print formatting options
 * @returns Print-ready quote data
 */
export function prepareQuoteDataForPrint(
  quoteResults: CancelQuoteResult[],
  submittedQuoteRequest: GetCancelQuoteRequest,
  selectedContracts: ContractDetail[],
  cancelReasons: CancelReason[],
  options: PrintTableOptions = DEFAULT_PRINT_OPTIONS
): PrintQuoteData {
  // Helper to find cancel reason description
  const getCancelReasonDescription = (reasonId: number): string => {
    const reason = cancelReasons.find((r) => r.id === reasonId);
    return reason ? reason.description : 'Unknown Reason';
  };

  // Extract header information (assuming all contracts share the same customer/vehicle)
  const primaryContract = selectedContracts[0];
  const customerName = primaryContract?.customerFullName || 'N/A';
  const vehicleInfo = primaryContract
    ? `${primaryContract.vehicleInfo.year} ${primaryContract.vehicleInfo.make} ${primaryContract.vehicleInfo.model} (VIN: ${primaryContract.vehicleInfo.vin})`
    : 'N/A';

  const headerData = {
    customerName,
    vehicleInfo,
    cancelDate: formatDateForPrint(submittedQuoteRequest.cancelDate),
    cancelMileage: submittedQuoteRequest.cancelMileage.toLocaleString(),
    cancelReason: getCancelReasonDescription(
      submittedQuoteRequest.cancelReasonId
    ),
    quoteDate: formatDateForPrint(new Date()),
  };

  // Organize quotes into successful and denied
  const { successfulQuotes, deniedQuotes } = organizeQuotesForPrintTables(
    quoteResults,
    selectedContracts,
    options
  );

  return {
    ...headerData,
    successfulQuotes,
    deniedQuotes,
  };
}

/**
 * Organizes quote results into separate arrays for successful and denied quotes,
 * formatted specifically for print table display.
 *
 * @param quoteResults - Array of quote results
 * @param selectedContracts - Contract details for additional information
 * @param options - Print formatting options
 * @returns Object containing organized successful and denied quotes
 */
export function organizeQuotesForPrintTables(
  quoteResults: CancelQuoteResult[],
  selectedContracts: ContractDetail[],
  options: PrintTableOptions = DEFAULT_PRINT_OPTIONS
): {
  successfulQuotes: PrintSuccessfulQuote[];
  deniedQuotes: PrintDeniedQuote[];
} {
  const successfulQuotes: PrintSuccessfulQuote[] = [];
  const deniedQuotes: PrintDeniedQuote[] = [];

  // Helper to find contract details
  const getContractDetails = (
    contractNumber: string
  ): ContractDetail | undefined => {
    return selectedContracts.find((c) => c.contractNumber === contractNumber);
  };

  // Helper to format dealer name with proper capitalization
  const formatDealerName = (dealerName: string): string => {
    return dealerName
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  quoteResults.forEach((result) => {
    const contractDetails = getContractDetails(result.contractNumber);
    const dealerName = contractDetails
      ? formatDealerName(contractDetails.issuingDealer)
      : 'N/A';

    if (result.status === 'quoted') {
      const successResult = result as CancelQuoteSuccess;
      const factors =
        successResult.allFactors?.join(', ') || successResult.factor || 'N/A';

      // Determine text handling for dealer name and product type
      const dealerTextHandling = getOptimalTextHandling(
        'important',
        options.maxDealerNameLength,
        dealerName.length
      );
      const productTextHandling = getOptimalTextHandling(
        'standard',
        15,
        result.productTypeName.length
      );

      successfulQuotes.push({
        contractNumber: result.contractNumber,
        productType: result.productTypeName,
        issuingDealer: dealerName,
        effectiveDate: formatDateForPrint(successResult.effectiveDate),
        planTerm: successResult.planTerm,
        proRataFactor: factors,
        originalPrice: formatCurrencyForPrint(successResult.customerPrice),
        cancellationFee: formatCurrencyForPrint(successResult.fee),
        claimsDeducted: formatCurrencyForPrint(successResult.totalClaims),
        refundAmount: formatCurrencyForPrint(successResult.customerRefund),
        textHandling: {
          issuingDealer: dealerTextHandling,
          productType: productTextHandling,
        },
      });
    } else if (result.status === 'denied') {
      const deniedResult = result as CancelQuoteDenied;

      // For denied quotes, handle dealer name, product type, and denial reason
      const dealerTextHandling = getOptimalTextHandling(
        'important',
        options.maxDealerNameLength,
        dealerName.length
      );
      const productTextHandling = getOptimalTextHandling(
        'standard',
        20,
        result.productTypeName.length
      );
      const denialTextHandling = getOptimalTextHandling(
        'critical', // Critical content - we want to preserve denial reasons
        50,
        deniedResult.denialReason.length
      );

      deniedQuotes.push({
        contractNumber: result.contractNumber,
        productType: result.productTypeName,
        issuingDealer: dealerName,
        denialReason: deniedResult.denialReason,
        textHandling: {
          issuingDealer: dealerTextHandling,
          denialReason: denialTextHandling,
          productType: productTextHandling,
        },
      });
    }
  });

  return { successfulQuotes, deniedQuotes };
}

/**
 * Processes text content for print display based on the specified strategy.
 * This function handles both wrapping and truncation strategies.
 *
 * @param text - Text content to process
 * @param config - Text handling configuration
 * @returns Object containing processed text and CSS classes for styling
 */
export function processTextForPrint(
  text: string,
  config: PrintTextConfig
): {
  processedText: string;
  cssClasses: string[];
  shouldWrap: boolean;
} {
  if (!text) {
    return {
      processedText: 'N/A',
      cssClasses: [],
      shouldWrap: false,
    };
  }

  const cssClasses: string[] = [];
  let processedText = text;
  let shouldWrap = false;

  switch (config.strategy) {
    case 'wrap':
      shouldWrap = true;
      cssClasses.push(
        'print:break-words',
        'print:whitespace-normal',
        'print:align-top'
      );
      if (config.maxLines) {
        cssClasses.push(`print:line-clamp-${config.maxLines}`);
      }
      break;

    case 'truncate':
      if (config.maxLength && text.length > config.maxLength) {
        processedText = text.substring(0, config.maxLength - 3) + '...';
      }
      cssClasses.push('print:truncate', 'print:align-middle');
      break;

    case 'auto':
    default:
      // Auto strategy: wrap if short enough, truncate if too long
      if (config.maxLength && text.length > config.maxLength) {
        if (
          config.priority === 'preserve-content' &&
          text.length < config.maxLength * 2
        ) {
          shouldWrap = true;
          cssClasses.push(
            'print:break-words',
            'print:whitespace-normal',
            'print:align-top'
          );
        } else {
          processedText = text.substring(0, config.maxLength - 3) + '...';
          cssClasses.push('print:truncate', 'print:align-middle');
        }
      }
      break;
  }

  return {
    processedText,
    cssClasses,
    shouldWrap,
  };
}

/**
 * Calculates optimal table layouts for 8.5x11 landscape format.
 * This function provides column width recommendations and layout suggestions
 * for both successful and denied quote tables, with support for text wrapping.
 *
 * @param options - Print table options
 * @returns Object containing layout recommendations for print tables
 */
export function calculateOptimalTableLayouts(
  options: PrintTableOptions = DEFAULT_PRINT_OPTIONS
) {
  const baseRowHeight = options.allowTextWrapping ? '2rem' : '1.5rem';
  const maxRowHeight = options.allowTextWrapping ? '4rem' : '2rem';

  return {
    // Layout for successful quotes table (10 columns)
    successfulQuotesTable: {
      pageOrientation: 'landscape' as const,
      maxColumns: 10,
      columnWidths: {
        contractNumber: '10%', // Contract
        productType: '12%', // Product
        issuingDealer: '12%', // Issuing Dealer
        effectiveDate: '10%', // Contract Effective Date
        planTerm: '8%', // Plan Term
        proRataFactor: '10%', // Pro-Rata Factor
        originalPrice: '10%', // Original Customer Price
        cancellationFee: '8%', // Cancellation Fee
        claimsDeducted: '10%', // Claims Deducted
        refundAmount: '10%', // Refund Amount
      },
      fontSize: 'text-xs',
      rowHeight: {
        min: baseRowHeight,
        max: maxRowHeight,
        allowVariable: options.allowTextWrapping,
      },
      maxRowsPerPage: options.allowTextWrapping ? 15 : 20, // Fewer rows if allowing wrapping
    },

    // Layout for denied quotes table (4 columns)
    deniedQuotesTable: {
      pageOrientation: 'landscape' as const,
      maxColumns: 4,
      columnWidths: {
        contractNumber: '20%', // Contract
        productType: '20%', // Product
        issuingDealer: '25%', // Issuing Dealer
        denialReason: '35%', // Denial Reason (larger for wrapping)
      },
      fontSize: 'text-sm',
      rowHeight: {
        min: baseRowHeight,
        max: options.preserveCriticalContent ? '6rem' : maxRowHeight, // Allow taller rows for denial reasons
        allowVariable: options.allowTextWrapping,
      },
      maxRowsPerPage: options.allowTextWrapping ? 12 : 25, // Fewer rows if allowing wrapping
    },

    // Common layout settings
    common: {
      pageMargins: '0.5in',
      headerHeight: '1.5in',
      footerHeight: '0.5in',
      tableBorderWidth: '1px',
      cellPadding: options.allowTextWrapping ? '6px' : '4px',
      verticalAlignment: options.allowTextWrapping ? 'top' : 'middle',
    },
  };
}

/**
 * Updated utility to handle long text content in print tables with wrapping support.
 * This is kept for backward compatibility but the new processTextForPrint function is preferred.
 *
 * @param text - Text content to process
 * @param columnType - Type of column (affects truncation strategy)
 * @param maxLength - Maximum character length before truncation
 * @param allowWrap - Whether to allow text wrapping instead of truncation
 * @returns Processed text suitable for print display
 */
export function handleLongTextForPrint(
  text: string,
  columnType: 'dealer' | 'denial' | 'general',
  maxLength: number = 50,
  allowWrap: boolean = true
): string {
  if (!text || text.length <= maxLength) {
    return text;
  }

  // For denial reasons, prefer wrapping to preserve content
  if (columnType === 'denial' && allowWrap) {
    return text; // Return full text, CSS will handle wrapping
  }

  // For dealer names, use smart truncation or return full text for wrapping
  if (columnType === 'dealer') {
    if (allowWrap && text.length < maxLength * 2) {
      return text; // Return full text for CSS wrapping
    }

    // Smart truncation for very long dealer names
    const dealerWords = text.split(' ');
    if (dealerWords.length > 1) {
      const firstWord = dealerWords[0];
      const lastWord = dealerWords[dealerWords.length - 1];
      const maxMiddleLength =
        maxLength - firstWord.length - lastWord.length - 4;

      if (maxMiddleLength > 0) {
        const middlePart = dealerWords.slice(1, -1).join(' ');
        if (middlePart.length <= maxMiddleLength) {
          return text;
        }
        return `${firstWord} ... ${lastWord}`;
      }
    }
  }

  // Fallback truncation
  return text.substring(0, maxLength - 3) + '...';
}

/**
 * Utility to calculate if content will fit on a single page and provide pagination recommendations.
 *
 * @param successfulQuotesCount - Number of successful quotes
 * @param deniedQuotesCount - Number of denied quotes
 * @param options - Print table options
 * @returns Object with pagination recommendations
 */
export function calculatePrintPagination(
  successfulQuotesCount: number,
  deniedQuotesCount: number,
  options: PrintTableOptions = DEFAULT_PRINT_OPTIONS
) {
  const layouts = calculateOptimalTableLayouts(options);

  const successfulQuotesPages = Math.ceil(
    successfulQuotesCount / layouts.successfulQuotesTable.maxRowsPerPage
  );
  const deniedQuotesPages = Math.ceil(
    deniedQuotesCount / layouts.deniedQuotesTable.maxRowsPerPage
  );

  return {
    totalPages: Math.max(1, successfulQuotesPages + deniedQuotesPages),
    successfulQuotesPages,
    deniedQuotesPages,
    needsPageBreaks:
      successfulQuotesCount > layouts.successfulQuotesTable.maxRowsPerPage ||
      deniedQuotesCount > layouts.deniedQuotesTable.maxRowsPerPage,
    recommendations: {
      splitSuccessfulQuotes:
        successfulQuotesCount > layouts.successfulQuotesTable.maxRowsPerPage,
      splitDeniedQuotes:
        deniedQuotesCount > layouts.deniedQuotesTable.maxRowsPerPage,
      separateTablesOnDifferentPages:
        successfulQuotesCount + deniedQuotesCount >
        (options.allowTextWrapping ? 20 : 30), // Lower threshold for wrapping
      useTextWrapping: options.allowTextWrapping,
      estimatedRowHeight: options.allowTextWrapping ? 'variable' : 'fixed',
    },
  };
}
